import { lab } from 'd3-color';

// Tableau 20 without its greys, plus darker blue, red, green and purple.
// Light/dark pairs of the same hue are the closest (~16 in CIELAB);
// assignment keeps such pairs apart from each other geographically.
export const KOMMUN_PALETTE = [
    '#4e79a7',
    '#a0cbe8',
    '#f28e2b',
    '#ffbe7d',
    '#59a14f',
    '#8cd17d',
    '#b6992d',
    '#f1ce63',
    '#499894',
    '#86bcb6',
    '#e15759',
    '#ff9d9a',
    '#d37295',
    '#fabfd2',
    '#b07aa1',
    '#6b4c9a',
    '#9d7660',
    '#d7b5a6',
    '#1f3f6e',
    '#7b2d3b',
    '#2e6b3a',
];

// Colors at least this far apart (CIELAB) count as clearly distinct, so
// beyond it we spread colors evenly rather than picking the most extreme one
const DISTINCT_NEIGHBOR = 30;
const DISTINCT_SECOND_NEIGHBOR = 20;

const N_NEIGHBORS = 6;

export type KommunLocation = {
    kommunnamn: string;
    coords?: [number, number];
};

function labDistance(a: string, b: string) {
    const la = lab(a);
    const lb = lab(b);
    return Math.hypot(la.l - lb.l, la.a - lb.a, la.b - lb.b);
}

/**
 * Builds a symmetric neighbor graph by connecting each kommun to its nearest
 * kommuner by centroid. Approximates shared borders without needing polygons.
 */
function getNeighbors(kommuner: Required<KommunLocation>[]) {
    const neighbors = new Map<string, Set<string>>(
        kommuner.map((k) => [k.kommunnamn, new Set()])
    );

    for (const k of kommuner) {
        // Scale longitude so distances are roughly isotropic at Swedish latitudes
        const cosLat = Math.cos((k.coords[1] * Math.PI) / 180);
        const nearest = kommuner
            .filter((o) => o !== k)
            .map((o) => ({
                kommunnamn: o.kommunnamn,
                distance: Math.hypot(
                    (o.coords[0] - k.coords[0]) * cosLat,
                    o.coords[1] - k.coords[1]
                ),
            }))
            .sort((a, b) => a.distance - b.distance)
            .slice(0, N_NEIGHBORS);

        for (const o of nearest) {
            neighbors.get(k.kommunnamn)!.add(o.kommunnamn);
            neighbors.get(o.kommunnamn)!.add(k.kommunnamn);
        }
    }

    return neighbors;
}

/**
 * Assigns a color to each kommun so that nearby kommuner get colors that are
 * as perceptually different as possible. Every kommun gets a unique color
 * while there are enough colors; beyond that colors are reused, but not next
 * to each other. Deterministic for a given set of kommuner.
 */
export function assignKommunColors(
    kommuner: KommunLocation[],
    palette: string[] = KOMMUN_PALETTE
): Map<string, string> {
    const sorted = [...kommuner].sort((a, b) =>
        a.kommunnamn.localeCompare(b.kommunnamn, 'sv')
    );
    const located = sorted.filter(
        (k): k is Required<KommunLocation> => k.coords !== undefined
    );
    const unlocated = sorted.filter((k) => k.coords === undefined);

    const neighbors = getNeighbors(located);
    const allowReuse = kommuner.length > palette.length;
    const assigned = new Map<string, string>();
    const usage = new Map<string, number>(palette.map((c) => [c, 0]));

    // Most constrained kommuner first (Welsh–Powell order)
    const order = [
        ...[...located].sort(
            (a, b) =>
                neighbors.get(b.kommunnamn)!.size -
                neighbors.get(a.kommunnamn)!.size
        ),
        ...unlocated,
    ];

    const colorsOf = (names: Iterable<string>) =>
        [...names]
            .map((n) => assigned.get(n))
            .filter((c): c is string => c !== undefined);
    const minDistance = (color: string, others: string[], cap: number) =>
        Math.min(cap, ...others.map((c) => labDistance(color, c)));

    for (const k of order) {
        const direct = neighbors.get(k.kommunnamn) ?? new Set<string>();
        const secondHop = new Set<string>();
        for (const n of direct) {
            for (const nn of neighbors.get(n)!) {
                if (nn !== k.kommunnamn && !direct.has(nn)) secondHop.add(nn);
            }
        }
        const directColors = colorsOf(direct);
        const secondHopColors = colorsOf(secondHop);

        const candidates = allowReuse
            ? palette
            : palette.filter((c) => usage.get(c) === 0);

        let best = candidates[0];
        let bestScore = -Infinity;
        for (const color of candidates) {
            // Distinct from direct neighbors first, then from their
            // neighbors, then the least used color
            const score =
                minDistance(color, directColors, DISTINCT_NEIGHBOR) * 1e6 +
                minDistance(color, secondHopColors, DISTINCT_SECOND_NEIGHBOR) *
                    1e3 -
                usage.get(color)!;
            if (score > bestScore) {
                best = color;
                bestScore = score;
            }
        }

        assigned.set(k.kommunnamn, best);
        usage.set(best, usage.get(best)! + 1);
    }

    return assigned;
}
